import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/TechCombobox";
import { FileSignature, ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertTriangle, FileText, Wand2 } from "lucide-react";

const STATUS_COLORS = {
  "Confirmed": "bg-emerald-100 text-emerald-800", "Client Confirmed": "bg-emerald-100 text-emerald-800",
  "ComplyX Reviewed": "bg-blue-100 text-blue-800", "Suggested": "bg-slate-100 text-slate-700",
  "Needs Management Decision": "bg-amber-100 text-amber-800", "Conflict Detected": "bg-red-100 text-red-800",
  "Missing": "bg-red-50 text-red-700", "Not Applicable": "bg-slate-100 text-slate-500",
};
const Badge = ({ s }) => <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;

export default function PolicyGenerator() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [ctx, setCtx] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [packages, setPackages] = useState({});
  const [selected, setSelected] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [edits, setEdits] = useState({});
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get("/recon/reconciliations").then((r) => {
      const m = {}; r.data.forEach((x) => { m[x.customer_id] = x.customer_name; });
      setCustomers(Object.entries(m).map(([id, name]) => ({ id, name })));
    }).catch(() => {});
    api.get("/policy/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    api.get("/policy/meta").then((r) => setPackages(r.data.packages || {})).catch(() => {});
  }, []);

  const loadCtx = useCallback(() => {
    if (!customerId) return;
    api.get(`/policy/generator/${customerId}/context`).then((r) => setCtx(r.data)).catch(() => {});
  }, [customerId]);
  useEffect(() => { loadCtx(); }, [loadCtx]);

  const toggle = (k) => setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const applyPackage = (pk) => setSelected(packages[pk]?.templates || []);

  const doResolve = async () => {
    try {
      const r = await api.post(`/policy/generator/${customerId}/resolve`, { template_keys: selected });
      setResolved(r.data); setEdits({}); setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resolve fields"); }
  };

  const saveGovernance = async () => {
    // persist edited governance/customer fields as client-confirmed values
    const fields = {};
    Object.entries(edits).forEach(([k, v]) => { fields[k] = { value: v, status: "Client Confirmed" }; });
    if (Object.keys(fields).length) {
      const existing = (await api.get(`/policy/governance/${customerId}`)).data.fields || {};
      await api.put(`/policy/governance/${customerId}`, { fields: { ...existing, ...fields } });
      toast.success("Field decisions saved.");
      const r = await api.post(`/policy/generator/${customerId}/resolve`, { template_keys: selected });
      setResolved(r.data); setEdits({});
    }
  };

  const generate = async () => {
    try {
      const r = await api.post(`/policy/generator/${customerId}/generate`, { template_keys: selected, field_overrides: {} });
      setJob({ id: r.data.job_id, status: "Queued", progress: 0 }); setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed to start"); }
  };

  useEffect(() => {
    if (!job?.id || job.status === "Complete" || job.status === "Failed") return;
    const iv = setInterval(async () => {
      try { const r = await api.get(`/policy/jobs/${job.id}`); setJob(r.data); if (["Complete", "Failed"].includes(r.data.status)) clearInterval(iv); } catch {}
    }, 1500);
    return () => clearInterval(iv);
  }, [job?.id, job?.status]);

  const groups = resolved?.grouped || {};
  const unresolved = resolved?.unresolved || [];

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <FileSignature className="h-6 w-6 text-gold" /> Policy &amp; Procedure Generator
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">Generate customer-specific policies and procedures from the master DOCX templates. Values are pulled from the customer record, Technology Profile, and Governance Intake. Documents are generated in the background with original formatting preserved.</p>
      </header>

      <div className="p-6 md:p-8">
        <div className="flex items-center gap-2 mb-6 text-xs">
          {["Customer", "Documents", "Fields", "Preview", "Generate"].map((s, i) => (
            <div key={s} className={`flex items-center gap-2 ${step === i + 1 ? "text-navy font-semibold" : "text-slate-400"}`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] ${step >= i + 1 ? "bg-gold text-navy" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>{s}
              {i < 4 && <ChevronRight className="h-4 w-4" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="max-w-md space-y-4" data-testid="policy-step-customer">
            <Label className="text-xs">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="policy-customer"><SelectValue placeholder="Select a customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {ctx && (
              <div className="rounded border border-border p-4 text-sm space-y-1.5 bg-card">
                <div className="font-semibold text-navy">{ctx.customer_name}</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <span>CMMC level: <b className="text-navy">{ctx.cmmc_level || "—"}</b></span>
                  <span>SSP: <b className="text-navy">{ctx.ssp_version || "—"}</b></span>
                  <span>Tech profile systems: <b className="text-navy">{ctx.tech_profile_capabilities}</b></span>
                  <span>Governance answers: <b className="text-navy">{ctx.governance_answered}</b></span>
                  <span>Existing documents: <b className="text-navy">{ctx.existing_documents?.length || 0}</b></span>
                </div>
              </div>
            )}
            <Button className="bg-navy text-white hover:bg-navy-700" disabled={!customerId} onClick={() => setStep(2)} data-testid="policy-next-1">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="policy-step-documents">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground mr-2">Predefined packages:</span>
              {Object.entries(packages).map(([k, v]) => <Button key={k} size="sm" variant="outline" onClick={() => applyPackage(k)} data-testid={`policy-pkg-${k}`}>{v.label}</Button>)}
              <Button size="sm" variant="ghost" onClick={() => setSelected(templates.map((t) => t.key))}>Select all</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {templates.map((t) => (
                <label key={t.key} className={`flex items-start gap-2 rounded border p-3 cursor-pointer text-sm ${selected.includes(t.key) ? "border-gold bg-gold/5" : "border-border"}`} data-testid={`policy-tpl-${t.key}`}>
                  <Checkbox checked={selected.includes(t.key)} onCheckedChange={() => toggle(t.key)} className="mt-0.5" />
                  <div><div className="font-medium text-navy">{t.title}</div><div className="text-[11px] text-muted-foreground">{t.category} · {t.placeholders?.length || 0} fields · {t.status}</div></div>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button className="bg-navy text-white hover:bg-navy-700" disabled={selected.length === 0} onClick={doResolve} data-testid="policy-next-2">Review Fields ({selected.length}) <ChevronRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 3 && resolved && (
          <div className="space-y-4" data-testid="policy-step-fields">
            {unresolved.length > 0 && <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {unresolved.length} required field(s) still need a management decision. You may generate a draft (they will show as "Needs Management Decision"), but approval will be blocked until resolved.</div>}
            {Object.entries(groups).map(([g, fields]) => (
              <div key={g} className="rounded border border-border">
                <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-navy">{g}</div>
                <div className="divide-y divide-border/60">
                  {fields.map((f) => (
                    <div key={f.field_key} className="px-3 py-2.5 grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-center text-sm" data-testid={`policy-field-${f.field_key}`}>
                      <div><div className="font-medium text-navy text-xs">{f.label}</div><div className="text-[10px] text-muted-foreground">{f.source || "—"}</div></div>
                      {f.allowed_values?.length ? (
                        <Combobox value={edits[f.field_key] ?? f.value} onChange={(v) => setEdits((e) => ({ ...e, [f.field_key]: v }))} options={f.allowed_values.filter((o) => o !== "Custom" && !o.includes("Add Custom"))} allowCustom customLabel="Use custom" placeholder="Select…" testid={`policy-val-${f.field_key}`} />
                      ) : (
                        <Input value={edits[f.field_key] ?? f.value} onChange={(e) => setEdits((s) => ({ ...s, [f.field_key]: e.target.value }))} className="h-8 text-xs" data-testid={`policy-val-${f.field_key}`} />
                      )}
                      <Badge s={edits[f.field_key] ? "Client Confirmed" : f.status} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              {Object.keys(edits).length > 0 && <Button variant="outline" onClick={saveGovernance} data-testid="policy-save-fields"><CheckCircle2 className="h-4 w-4 mr-1" /> Save decisions</Button>}
              <Button className="bg-navy text-white hover:bg-navy-700" onClick={() => setStep(4)} data-testid="policy-next-3">Impact Preview <ChevronRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 4 && resolved && (
          <div className="space-y-4" data-testid="policy-step-preview">
            <div className="rounded border border-border max-h-[26rem] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0"><tr className="bg-slate-50 border-b text-left">{["Template", "Placeholder", "Value", "Source", "Status"].map((h) => <th key={h} className="px-3 py-2 font-semibold text-navy">{h}</th>)}</tr></thead>
                <tbody>
                  {resolved.impact.map((r, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-1.5 text-navy">{r.template}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{r.placeholder}</td>
                      <td className="px-3 py-1.5 max-w-[220px] truncate">{r.value}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.source}</td>
                      <td className="px-3 py-1.5"><Badge s={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(3)}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={generate} data-testid="policy-generate"><Wand2 className="h-4 w-4 mr-1.5" /> Generate {selected.length} Document(s)</Button>
            </div>
          </div>
        )}

        {step === 5 && job && (
          <div className="max-w-xl space-y-4" data-testid="policy-step-generate">
            {job.status !== "Complete" && job.status !== "Failed" && (
              <div className="rounded border border-border p-5 space-y-2">
                <div className="flex items-center gap-2 text-navy text-sm"><Loader2 className="h-4 w-4 animate-spin text-gold-dark" /> {job.stage || "Queued"} — generating in the background. You may leave and return.</div>
                <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-gold transition-all duration-500" style={{ width: `${job.progress || 5}%` }} /></div>
              </div>
            )}
            {job.status === "Complete" && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-5 space-y-3" data-testid="policy-gen-complete">
                <div className="flex items-center gap-2 text-emerald-800 font-medium"><CheckCircle2 className="h-5 w-5" /> Generated {job.results?.length || 0} document(s).</div>
                <div className="space-y-1.5">
                  {job.results?.map((r) => (
                    <div key={r.document_id} className="flex items-center justify-between text-xs bg-white rounded border border-border px-3 py-2">
                      <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-navy" /> {r.title}</span>
                      <span className="text-muted-foreground">{r.unresolved ? `${r.unresolved} unresolved` : "no unresolved"} · {r.opens ? "opens OK" : "open error"}</span>
                    </div>
                  ))}
                </div>
                <Button className="bg-navy text-white hover:bg-navy-700" onClick={() => nav("/operations/customer-documents")} data-testid="policy-view-docs">View in Customer Documents <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
            )}
            {job.status === "Failed" && <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">Generation failed: {job.error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
